'use client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useCookieConsent } from '../../context/cookieConsentContext';
import { useTranslation } from '../../i18n/client';

// This function is kept for backward compatibility
export function cookieConsentGiven(): 'accepted' | 'rejected' | 'undecided' {
  if (typeof window === 'undefined') {
    return 'undecided';
  }

  try {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      return 'undecided';
    }

    // Handle both new union type values and legacy values
    switch (consent) {
      case 'accepted':
        return 'accepted';
      case 'rejected':
        return 'rejected';
      case 'yes':
        return 'accepted';
      case 'no':
        return 'rejected';
      default:
        return 'undecided';
    }
  } catch (error) {
    // Handle SecurityError (private browsing) or QuotaExceededError
    console.warn('Failed to read consent from localStorage:', error);
    return 'undecided';
  }
}

export default function ConsentBanner() {
  const pathname = usePathname();
  const lng = pathname.split('/')[1] || 'en';
  const { t } = useTranslation(lng, 'consent');
  const { consentGiven, isMounted, acceptCookies, declineCookies } = useCookieConsent();
  const posthog = usePostHog();

  // Update PostHog configuration based on consent
  useEffect(() => {
    if (consentGiven !== 'undecided' && posthog) {
      try {
        if (consentGiven === 'accepted') {
          posthog.opt_in_capturing();
          posthog.set_config({
            persistence: 'localStorage+cookie',
          });
        } else if (consentGiven === 'rejected') {
          posthog.opt_out_capturing();
          posthog.set_config({
            persistence: 'memory',
          });
        }
      } catch (error) {
        console.error('Failed to set PostHog configuration:', error);
      }
    }
  }, [consentGiven, posthog]);

  const handleAcceptCookies = async () => {
    await acceptCookies();
  };

  const handleDeclineCookies = async () => {
    await declineCookies();
  };

  // Don't render anything until mounted and we've checked localStorage
  if (!isMounted) {
    return null;
  }

  // Don't show banner if consent has been given or declined
  if (consentGiven === 'accepted' || consentGiven === 'rejected') {
    return null;
  }

  return (
    <div className="fixed right-0 bottom-0 left-0 z-50 border-t border-gray-200 bg-white shadow-lg">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <h3 className="mb-1 text-sm font-medium text-gray-900">{t('title')}</h3>
            <p className="text-sm text-gray-600">{t('description')}</p>
            <div className="flex flex-row">
              <p className="text-sm text-gray-600">{t('description2')}</p>
              <Link
                href={
                  lng === 'de'
                    ? 'https://www.docomondo.com/de-datenschutz'
                    : 'https://docomondo.com/en-privacy'
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-cPrimary ml-1 text-sm underline sm:text-sm sm:underline"
              >
                {t('hyperlink')}
              </Link>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={handleDeclineCookies}
              className="focus:ring-cPrimary rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:outline-none"
            >
              {t('decline')}
            </button>
            <button
              type="button"
              onClick={handleAcceptCookies}
              className="bg-cPrimary hover:bg-cPrimary-700 focus:ring-cPrimary-500 rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white focus:ring-2 focus:ring-offset-2 focus:outline-none"
            >
              {t('accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
